import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { Plus, Save, Trash2, Edit3, Activity, BarChart3 } from "lucide-react";

export default function AdminLive() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const [m, u, t] = await Promise.all([
      api.get("/matches"),
      api.get("/users"),
      api.get("/tournaments"),
    ]);
    setItems(m.data);
    setUsers(u.data);
    setTournaments(t.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6 fade-in" data-testid="admin-live-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Live Matches</div>
          <h1 className="font-display text-3xl tracking-tight mt-2">Match-Steuerung</h1>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary" data-testid="create-match-btn">
          <Plus className="w-4 h-4" /> Neues Match
        </button>
      </div>

      {error && <div className="text-red-400 text-sm font-mono">{error}</div>}

      {creating && (
        <MatchForm
          users={users}
          tournaments={tournaments}
          onCancel={() => setCreating(false)}
          onSave={async (data) => {
            try {
              await api.post("/matches", data);
              setCreating(false);
              load();
            } catch (e) {
              setError(formatApiError(e));
            }
          }}
        />
      )}

      <div className="space-y-3">
        {items.map((m) => (
          editing === m.id ? (
            <div key={m.id} className="card-solid p-5">
              <MatchForm
                users={users}
                tournaments={tournaments}
                initial={m}
                onCancel={() => setEditing(null)}
                onSave={async (data) => {
                  await api.put(`/matches/${m.id}`, data);
                  setEditing(null);
                  load();
                }}
              />
              <div className="border-t border-white/10 my-6" />
              <MatchControl match={m} onChanged={load} />
            </div>
          ) : (
            <MatchRow key={m.id} match={m} onEdit={() => setEditing(m.id)} onChanged={load} />
          )
        ))}
      </div>
    </div>
  );
}

function MatchForm({ initial, users, tournaments, onSave, onCancel }) {
  const [form, setForm] = useState(initial ? {
    tournament_id: initial.tournament_id || "",
    home_user_id: initial.home_user_id || "",
    away_user_id: initial.away_user_id || "",
    home_name: initial.home_name,
    away_name: initial.away_name,
    home_team: initial.home_team || "Home",
    away_team: initial.away_team || "Away",
    start_time: initial.start_time ? new Date(initial.start_time).toISOString().slice(0, 16) : "",
    notes: initial.notes || "",
  } : {
    tournament_id: "",
    home_user_id: "",
    away_user_id: "",
    home_name: "",
    away_name: "",
    home_team: "Home FC",
    away_team: "Away FC",
    start_time: new Date(Date.now() + 3600000).toISOString().slice(0, 16),
    notes: "",
  });

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      tournament_id: form.tournament_id || null,
      home_user_id: form.home_user_id || null,
      away_user_id: form.away_user_id || null,
      start_time: new Date(form.start_time).toISOString(),
    });
  };

  const setUserField = (side, uid) => {
    const u = users.find((x) => x.user_id === uid);
    setForm({ ...form, [`${side}_user_id`]: uid, [`${side}_name`]: u?.name || form[`${side}_name`] });
  };

  return (
    <form onSubmit={submit} className="card-solid p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="md:col-span-2 font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">
        {initial ? "Match bearbeiten" : "Neues Match"}
      </div>
      <div>
        <label className="label">Turnier (optional)</label>
        <select className="input" value={form.tournament_id} onChange={(e) => setForm({ ...form, tournament_id: e.target.value })}>
          <option value="">— keines —</option>
          {tournaments.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
      <div>
        <label className="label">Anstoß</label>
        <input type="datetime-local" required className="input" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
      </div>
      <div>
        <label className="label">Home Spieler</label>
        <select className="input" value={form.home_user_id} onChange={(e) => setUserField("home", e.target.value)}>
          <option value="">— frei —</option>
          {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
        </select>
      </div>
      <div>
        <label className="label">Away Spieler</label>
        <select className="input" value={form.away_user_id} onChange={(e) => setUserField("away", e.target.value)}>
          <option value="">— frei —</option>
          {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
        </select>
      </div>
      {[
        ["home_name", "Home Anzeigename"],
        ["away_name", "Away Anzeigename"],
        ["home_team", "Home Team (FC)"],
        ["away_team", "Away Team (FC)"],
      ].map(([k, l]) => (
        <div key={k}>
          <label className="label">{l}</label>
          <input required className="input" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
        </div>
      ))}
      <div className="md:col-span-2">
        <label className="label">Notizen</label>
        <textarea className="input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </div>
      <div className="md:col-span-2 flex gap-2">
        <button type="submit" className="btn-primary"><Save className="w-4 h-4" /> Speichern</button>
        <button type="button" onClick={onCancel} className="btn-secondary">Abbrechen</button>
      </div>
    </form>
  );
}

function MatchRow({ match, onEdit, onChanged }) {
  const remove = async () => {
    if (!window.confirm("Match löschen?")) return;
    await api.delete(`/matches/${match.id}`);
    onChanged();
  };
  return (
    <div className="card-solid p-4 flex items-center gap-3" data-testid={`admin-match-${match.id}`}>
      <span className={`chip ${
        match.status === "live" ? "border-red-400/40 text-red-400" :
        match.status === "finished" ? "border-accent/40 text-accent" :
        "border-white/20 text-white/60"
      }`}>{match.status}</span>
      <div className="flex-1 min-w-0">
        <div className="font-display text-base">{match.home_name} vs {match.away_name}</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
          {match.home_team} vs {match.away_team} · {new Date(match.start_time).toLocaleString("de-DE")}
        </div>
      </div>
      <div className="font-display text-2xl tabular-nums text-gradient">
        {match.home_score} · {match.away_score}
      </div>
      <button onClick={onEdit} className="btn-ghost !p-2"><Edit3 className="w-4 h-4" /></button>
      <button onClick={remove} className="btn-ghost !p-2 text-red-400"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}

function MatchControl({ match, onChanged }) {
  const [score, setScore] = useState({ home: match.home_score, away: match.away_score, minute: match.minute, status: match.status });
  const [event, setEvent] = useState({ minute: 1, type: "goal", team: "home", player: "", detail: "" });
  const [stats, setStats] = useState(match.stats || {});
  const [showStats, setShowStats] = useState(false);

  const saveScore = async () => {
    await api.put(`/matches/${match.id}/score`, {
      home_score: score.home,
      away_score: score.away,
      minute: score.minute,
      status: score.status,
    });
    onChanged();
  };

  const addEvent = async () => {
    await api.post(`/matches/${match.id}/events`, event);
    setEvent({ ...event, player: "", detail: "" });
    onChanged();
  };

  const removeEvent = async (eid) => {
    await api.delete(`/matches/${match.id}/events/${eid}`);
    onChanged();
  };

  const saveStats = async () => {
    await api.put(`/matches/${match.id}/stats`, stats);
    setShowStats(false);
    onChanged();
  };

  return (
    <div className="space-y-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient flex items-center gap-2">
        <Activity className="w-3.5 h-3.5" /> Live Steuerung
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["home", "Home Score", score.home],
          ["away", "Away Score", score.away],
          ["minute", "Minute", score.minute],
        ].map(([k, l, v]) => (
          <div key={k}>
            <label className="label">{l}</label>
            <input type="number" className="input" value={v} onChange={(e) => setScore({ ...score, [k]: parseInt(e.target.value || "0", 10) })} data-testid={`live-score-${k}`} />
          </div>
        ))}
        <div>
          <label className="label">Status</label>
          <select className="input" value={score.status} onChange={(e) => setScore({ ...score, status: e.target.value })} data-testid="live-status">
            <option value="scheduled">scheduled</option>
            <option value="live">live</option>
            <option value="finished">finished</option>
          </select>
        </div>
      </div>
      <button onClick={saveScore} className="btn-primary" data-testid="save-score-btn"><Save className="w-4 h-4" /> Score speichern</button>

      <div className="border-t border-white/10 pt-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient mb-3">Event hinzufügen</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <input type="number" placeholder="Min" className="input" value={event.minute} onChange={(e) => setEvent({ ...event, minute: parseInt(e.target.value || "0", 10) })} />
          <select className="input" value={event.type} onChange={(e) => setEvent({ ...event, type: e.target.value })}>
            {["goal","assist","yellow","red","sub_in","sub_out","penalty","miss","save","var","kickoff","halftime","fulltime","note"].map((x) => <option key={x}>{x}</option>)}
          </select>
          <select className="input" value={event.team} onChange={(e) => setEvent({ ...event, team: e.target.value })}>
            <option value="home">home</option>
            <option value="away">away</option>
            <option value="neutral">neutral</option>
          </select>
          <input className="input" placeholder="Spieler" value={event.player} onChange={(e) => setEvent({ ...event, player: e.target.value })} />
          <input className="input" placeholder="Detail" value={event.detail} onChange={(e) => setEvent({ ...event, detail: e.target.value })} />
        </div>
        <div className="mt-2">
          <button onClick={addEvent} className="btn-secondary" data-testid="add-event-btn"><Plus className="w-4 h-4" /> Event hinzufügen</button>
        </div>
        {(match.events || []).length > 0 && (
          <div className="mt-3 space-y-1.5 text-sm font-mono">
            {match.events.map((e) => (
              <div key={e.id} className="flex items-center gap-3 p-2 bg-white/5 rounded-sm">
                <span className="tabular-nums w-10">{e.minute}'</span>
                <span className="uppercase tracking-[0.2em] text-[10px] w-20 text-white/60">{e.type}</span>
                <span className="flex-1">{e.player || "—"} {e.detail && `(${e.detail})`}</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{e.team}</span>
                <button onClick={() => removeEvent(e.id)} className="btn-ghost !p-1.5 text-red-400">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-white/10 pt-4">
        <button onClick={() => setShowStats((s) => !s)} className="btn-secondary" data-testid="toggle-stats-editor">
          <BarChart3 className="w-4 h-4" /> {showStats ? "Stats schließen" : "Stats bearbeiten"}
        </button>
        {showStats && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
            {[
              "possession_home","possession_away","shots_home","shots_away","shots_on_target_home","shots_on_target_away",
              "passes_home","passes_away","pass_accuracy_home","pass_accuracy_away","corners_home","corners_away",
              "fouls_home","fouls_away","yellow_home","yellow_away","red_home","red_away",
              "offsides_home","offsides_away","tackles_home","tackles_away","interceptions_home","interceptions_away",
              "saves_home","saves_away","xg_home","xg_away","xa_home","xa_away",
            ].map((k) => (
              <div key={k}>
                <label className="label !text-[9px]">{k}</label>
                <input type="number" step="0.1" className="input" value={stats[k] || 0} onChange={(e) => setStats({ ...stats, [k]: parseFloat(e.target.value || "0") })} />
              </div>
            ))}
            <div className="md:col-span-3">
              <button onClick={saveStats} className="btn-primary" data-testid="save-stats-btn"><Save className="w-4 h-4" /> Stats speichern</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
